import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { I18nService } from '../../../core';
import { TenantService } from '../../../core/tenant/tenant.service';
import { ToastService } from '../../../shared/ui';
import { CustomFieldsSchemaDataService } from '../services/custom-fields-schema-data.service';
import type { ProjectReusableFieldListItem } from '../contracts/workflow-custom-fields.contract';
import type {
  CustomFieldEditorState,
  CustomFieldType,
  CustomFieldViewModel,
} from '../models/custom-field.model';
import { clampFormulaResultDecimalPlaces } from '../models/formula-field.model';
import {
  createEmptyCustomField,
  normalizeFieldOptions,
} from '../utils/custom-field-editor.util';
import { clampRatingLevels } from '../utils/rating-field.util';
import { clampNumberFieldDecimalPlaces } from '../utils/number-field.util';
import {
  clampCodePrefix,
  normalizeCodeFormat,
  normalizeCodeLength,
  normalizeCodeSeparator,
  CODE_FORMAT_DEFAULT,
  CODE_LENGTH_DEFAULT,
  CODE_PREFIX_DEFAULT,
  CODE_SEPARATOR_DEFAULT,
} from '../utils/code-field.util';

type FieldTypeFilter = 'ALL' | CustomFieldType;

@Injectable({ providedIn: 'root' })
export class CustomFieldsFacade {
  private readonly i18n = inject(I18nService);
  private readonly tenant = inject(TenantService);
  private readonly toast = inject(ToastService);
  private readonly schemaService = inject(CustomFieldsSchemaDataService);
  private readonly t = this.i18n.t.bind(this.i18n);

  private readonly workflowIdSignal = signal<string | null>(null);
  private readonly projectIdSignal = signal<string | null>(null);
  private readonly fieldsSignal = signal<CustomFieldViewModel[]>([]);
  private readonly loadingSignal = signal<boolean>(false);
  private readonly errorSignal = signal<string | null>(null);
  /** Erros de salvar/excluir ou validação antes da API (não confundir com erro de carga). */
  private readonly fieldMutationErrorSignal = signal<string | null>(null);
  private readonly searchQuerySignal = signal<string>('');
  private readonly typeFilterSignal = signal<FieldTypeFilter>('ALL');
  private readonly selectedPreviewFieldIdSignal = signal<string | null>(null);
  private readonly editorStateSignal = signal<CustomFieldEditorState | null>(null);
  readonly reusablePickerOpen = signal(false);
  readonly reusablePickerLoading = signal(false);
  readonly reusablePickerError = signal<string | null>(null);
  readonly reusablePickerItems = signal<ProjectReusableFieldListItem[]>([]);
  readonly reusableAttachFieldId = signal<string | null>(null);
  /** Campo cuja linha está destacada / preview à direita (null até o usuário clicar). */
  readonly selectedPreviewFieldId = this.selectedPreviewFieldIdSignal.asReadonly();
  readonly workflowId = this.workflowIdSignal.asReadonly();
  readonly projectId = this.projectIdSignal.asReadonly();
  readonly loading = this.loadingSignal.asReadonly();
  readonly error = this.errorSignal.asReadonly();
  readonly fieldMutationError = this.fieldMutationErrorSignal.asReadonly();
  readonly searchQuery = this.searchQuerySignal.asReadonly();
  readonly typeFilter = this.typeFilterSignal.asReadonly();
  readonly editorState = this.editorStateSignal.asReadonly();

  readonly fields = computed(() =>
    [...this.fieldsSignal()].sort((a, b) => a.order - b.order)
  );

  readonly filteredFields = computed(() => {
    const q = this.searchQuerySignal().trim().toLowerCase();
    const typeFilter = this.typeFilterSignal();

    return this.fields().filter((field) => {
      const matchesQuery = q ? field.name.toLowerCase().includes(q) : true;
      const matchesType = typeFilter === 'ALL' ? true : field.type === typeFilter;
      return matchesQuery && matchesType;
    });
  });

  readonly isEmpty = computed(
    () => !this.loading() && !this.error() && this.fields().length === 0
  );

  readonly isFilteredEmpty = computed(
    () =>
      !this.loading() &&
      !this.error() &&
      this.fields().length > 0 &&
      this.filteredFields().length === 0
  );

  /** Só há preview quando o usuário clica numa linha; entrada na página não seleciona nada. */
  readonly selectedPreviewField = computed(() => {
    const selectedId = this.selectedPreviewFieldIdSignal();
    const filtered = this.filteredFields();
    if (!selectedId || !filtered.length) return null;
    return filtered.find((field) => field.fieldId === selectedId) ?? null;
  });

  constructor() {
    effect(() => {
      const companyId = this.tenant.companyId();
      if (!companyId) {
        this.reset();
      }
    });
  }

  loadWorkflowFields(workflowId: string | null, projectId?: string | null): void {
    this.workflowIdSignal.set(workflowId);
    if (projectId !== undefined) {
      this.projectIdSignal.set(projectId);
    }
    this.loadingSignal.set(true);
    this.errorSignal.set(null);

    if (!workflowId) {
      this.fieldsSignal.set([]);
      this.errorSignal.set(this.t('customFields.error.invalidWorkflow'));
      this.loadingSignal.set(false);
      return;
    }

    void this.loadWorkflowFieldsInternal(workflowId);
  }

  setProjectId(projectId: string | null): void {
    this.projectIdSignal.set(projectId);
  }

  retry(): void {
    this.loadWorkflowFields(this.workflowIdSignal());
  }

  setSearchQuery(value: string): void {
    this.searchQuerySignal.set(value);
  }

  setTypeFilter(value: FieldTypeFilter): void {
    this.typeFilterSignal.set(value);
  }

  openCreateEditor(): void {
    this.fieldMutationErrorSignal.set(null);
    const nextOrder = this.fields().length + 1;
    this.editorStateSignal.set({
      mode: 'create',
      open: true,
      field: createEmptyCustomField(nextOrder),
    });
  }

  openReusablePicker(): void {
    const pid = this.projectIdSignal();
    if (!pid) {
      this.fieldMutationErrorSignal.set(this.t('customFields.reusable.noProjectContext'));
      return;
    }
    this.fieldMutationErrorSignal.set(null);
    this.reusablePickerOpen.set(true);
    void this.loadReusablePickerItems();
  }

  closeReusablePicker(): void {
    this.reusablePickerOpen.set(false);
    this.reusablePickerError.set(null);
    this.reusablePickerItems.set([]);
    this.reusableAttachFieldId.set(null);
  }

  retryReusablePicker(): void {
    void this.loadReusablePickerItems();
  }

  attachProjectReusableField(projectReusableFieldId: string): void {
    void this.attachProjectReusableFieldInternal(projectReusableFieldId);
  }

  openEditEditor(fieldId: string): void {
    void this.openEditEditorAsync(fieldId);
  }

  private async openEditEditorAsync(fieldId: string): Promise<void> {
    this.fieldMutationErrorSignal.set(null);
    const workflowId = this.workflowIdSignal();
    let current = this.fields().find((item) => item.fieldId === fieldId);
    if (!current || !workflowId) return;

    if (current.type === 'FORMULA') {
      try {
        const fresh = await this.schemaService.getWorkflowCustomFieldById(fieldId);
        if (fresh) current = fresh;
      } catch {
        /* lista local continua válida */
      }
    }

    this.editorStateSignal.set({
      mode: 'edit',
      open: true,
      field: {
        ...current,
        options: [...current.options].sort((a, b) => a.order - b.order),
      },
    });
    this.selectedPreviewFieldIdSignal.set(fieldId);
  }

  closeEditor(): void {
    this.fieldMutationErrorSignal.set(null);
    this.editorStateSignal.set(null);
  }

  saveField(
    field: CustomFieldViewModel,
    options?: { saveAsProjectReusable?: boolean }
  ): void {
    void this.saveFieldInternal(field, options);
  }

  deleteField(fieldId: string): void {
    void this.deleteFieldInternal(fieldId);
  }

  selectPreviewField(fieldId: string): void {
    this.selectedPreviewFieldIdSignal.set(fieldId);
  }

  reset(): void {
    this.workflowIdSignal.set(null);
    this.projectIdSignal.set(null);
    this.fieldsSignal.set([]);
    this.loadingSignal.set(false);
    this.errorSignal.set(null);
    this.searchQuerySignal.set('');
    this.typeFilterSignal.set('ALL');
    this.selectedPreviewFieldIdSignal.set(null);
    this.editorStateSignal.set(null);
    this.fieldMutationErrorSignal.set(null);
    this.reusablePickerOpen.set(false);
    this.reusablePickerLoading.set(false);
    this.reusablePickerError.set(null);
    this.reusablePickerItems.set([]);
    this.reusableAttachFieldId.set(null);
  }

  private async loadReusablePickerItems(): Promise<void> {
    const pid = this.projectIdSignal();
    if (!pid) return;

    this.reusablePickerLoading.set(true);
    this.reusablePickerError.set(null);
    try {
      const items = await this.schemaService.getProjectReusableFields(pid);
      const used = new Set(
        this.fields()
          .map((f) => f.projectReusableFieldId)
          .filter((id): id is string => !!id)
      );
      this.reusablePickerItems.set(items.filter((item) => !used.has(item.id)));
    } catch (error: unknown) {
      const fromApi = this.schemaService.extractApiErrorMessage(error);
      this.reusablePickerError.set(fromApi ?? this.t('customFields.reusable.loadFailed'));
      this.reusablePickerItems.set([]);
    } finally {
      this.reusablePickerLoading.set(false);
    }
  }

  private async attachProjectReusableFieldInternal(projectReusableFieldId: string): Promise<void> {
    const workflowId = this.workflowIdSignal();
    if (!workflowId) return;

    this.reusablePickerError.set(null);
    this.reusableAttachFieldId.set(projectReusableFieldId);
    try {
      await this.schemaService.linkProjectReusableFieldToWorkflow({
        workflowId,
        projectReusableFieldId,
      });
      this.toast.success(this.t('customFields.reusable.addedSuccess'));
      this.closeReusablePicker();
      await this.loadWorkflowFieldsInternal(workflowId);
    } catch (error: unknown) {
      const fromApi = this.schemaService.extractApiErrorMessage(error);
      this.reusablePickerError.set(fromApi ?? this.t('customFields.reusable.addFailed'));
    } finally {
      this.reusableAttachFieldId.set(null);
    }
  }

  private async saveFieldInternal(
    field: CustomFieldViewModel,
    options?: { saveAsProjectReusable?: boolean }
  ): Promise<void> {
    const workflowId = this.workflowIdSignal();
    if (!workflowId) return;

    const normalized: CustomFieldViewModel = {
      ...field,
      name: field.name.trim(),
      placeholder: field.placeholder?.trim() || null,
      options: this.needsOptions(field.type) ? normalizeFieldOptions(field.options) : [],
      formulaDefinition: field.type === 'FORMULA' ? field.formulaDefinition : null,
      formulaResultDecimalPlaces:
        field.type === 'FORMULA'
          ? clampFormulaResultDecimalPlaces(field.formulaResultDecimalPlaces)
          : 0,
      numberDecimalPlaces:
        field.type === 'NUMBER'
          ? clampNumberFieldDecimalPlaces(field.numberDecimalPlaces)
          : 0,
      numberShowPercentSuffix: field.type === 'NUMBER' && field.numberShowPercentSuffix === true,
      ratingLevels: clampRatingLevels(field.ratingLevels),
      codePrefix: field.type === 'CODE' ? clampCodePrefix(field.codePrefix) : CODE_PREFIX_DEFAULT,
      codeFormat: field.type === 'CODE' ? normalizeCodeFormat(field.codeFormat) : CODE_FORMAT_DEFAULT,
      codeLength: field.type === 'CODE' ? normalizeCodeLength(field.codeLength) : CODE_LENGTH_DEFAULT,
      codeSeparator: field.type === 'CODE' ? normalizeCodeSeparator(field.codeSeparator) : CODE_SEPARATOR_DEFAULT,
      projectReusableFieldId: field.projectReusableFieldId ?? null,
      fromProjectReusable: field.fromProjectReusable === true,
      personRestrictToWorkflowMembers:
        field.type === 'PERSON' && field.personRestrictToWorkflowMembers === true,
    };

    if (!normalized.name || !normalized.type) {
      this.fieldMutationErrorSignal.set(this.t('customFields.error.nameTypeRequired'));
      return;
    }

    if (!this.validateFieldKeyUniqueness(normalized, workflowId)) {
      return;
    }

    this.fieldMutationErrorSignal.set(null);
    try {
      if (this.editorStateSignal()?.mode === 'edit' && normalized.fieldId) {
        await this.schemaService.updateWorkflowCustomField({
          workflowId,
          fieldId: normalized.fieldId,
          field: {
            name: normalized.name,
            type: normalized.type,
            required: normalized.required,
            readOnly: normalized.readOnly,
            showInQuickView: normalized.showInQuickView,
            showInApp: normalized.showInApp,
            placeholder: normalized.placeholder,
            order: normalized.order,
            options: normalized.options,
            formulaDefinition:
              normalized.type === 'FORMULA' ? normalized.formulaDefinition : null,
            formulaResultDecimalPlaces: normalized.formulaResultDecimalPlaces,
            numberDecimalPlaces: normalized.numberDecimalPlaces,
            numberShowPercentSuffix: normalized.numberShowPercentSuffix,
            ratingLevels: normalized.ratingLevels,
            codePrefix: normalized.codePrefix,
            codeFormat: normalized.codeFormat,
            codeLength: normalized.codeLength,
            codeSeparator: normalized.codeSeparator,
            projectReusableFieldId: normalized.projectReusableFieldId ?? null,
            fromProjectReusable: normalized.fromProjectReusable === true,
            personRestrictToWorkflowMembers:
              normalized.type === 'PERSON' &&
              normalized.personRestrictToWorkflowMembers === true,
            collectViaWhatsApp: normalized.collectViaWhatsApp === true,
            whatsAppPrompt: normalized.whatsAppPrompt ?? null,
            whatsAppCollectionOrder: normalized.whatsAppCollectionOrder ?? null,
          },
          saveAsProjectReusable:
            options?.saveAsProjectReusable === true &&
            normalized.type !== 'FORMULA' &&
            normalized.type !== 'CODE' &&
            !normalized.projectReusableFieldId?.trim()
              ? true
              : undefined,
        });
        this.selectedPreviewFieldIdSignal.set(normalized.fieldId);
        this.toast.success(this.t('customFields.page.fieldSaved'));
      } else {
        const created = await this.schemaService.createWorkflowCustomField({
          workflowId,
          field: {
            name: normalized.name,
            type: normalized.type,
            required: normalized.required,
            readOnly: normalized.readOnly,
            showInQuickView: normalized.showInQuickView,
            showInApp: normalized.showInApp,
            placeholder: normalized.placeholder,
            order: normalized.order,
            options: normalized.options,
            formulaDefinition:
              normalized.type === 'FORMULA' ? normalized.formulaDefinition : null,
            formulaResultDecimalPlaces: normalized.formulaResultDecimalPlaces,
            numberDecimalPlaces: normalized.numberDecimalPlaces,
            numberShowPercentSuffix: normalized.numberShowPercentSuffix,
            ratingLevels: normalized.ratingLevels,
            codePrefix: normalized.codePrefix,
            codeFormat: normalized.codeFormat,
            codeLength: normalized.codeLength,
            codeSeparator: normalized.codeSeparator,
            projectReusableFieldId: null,
            fromProjectReusable: false,
            personRestrictToWorkflowMembers:
              normalized.type === 'PERSON' &&
              normalized.personRestrictToWorkflowMembers === true,
            collectViaWhatsApp: normalized.collectViaWhatsApp === true,
            whatsAppPrompt: normalized.whatsAppPrompt ?? null,
            whatsAppCollectionOrder: normalized.whatsAppCollectionOrder ?? null,
          },
          saveAsProjectReusable:
            options?.saveAsProjectReusable === true &&
            normalized.type !== 'FORMULA' &&
            normalized.type !== 'CODE'
              ? true
              : undefined,
        });
        this.selectedPreviewFieldIdSignal.set(created.fieldId);
        this.toast.success(this.t('customFields.page.fieldSaved'));
      }
      this.closeEditor();
      await this.loadWorkflowFieldsInternal(workflowId);
    } catch (error: unknown) {
      const fromApi = this.schemaService.extractApiErrorMessage(error);
      this.fieldMutationErrorSignal.set(fromApi ?? this.t('customFields.error.saveFailed'));
    }
  }

  private async deleteFieldInternal(fieldId: string): Promise<void> {
    const workflowId = this.workflowIdSignal();
    if (!workflowId) return;

    this.fieldMutationErrorSignal.set(null);
    try {
      await this.schemaService.deleteWorkflowCustomField({ workflowId, fieldId });
      if (this.selectedPreviewFieldIdSignal() === fieldId) {
        this.selectedPreviewFieldIdSignal.set(null);
      }
      await this.loadWorkflowFieldsInternal(workflowId);
    } catch (error: unknown) {
      const fromApi = this.schemaService.extractApiErrorMessage(error);
      this.fieldMutationErrorSignal.set(fromApi ?? this.t('customFields.error.deleteFailed'));
    }
  }

  private async loadWorkflowFieldsInternal(workflowId: string): Promise<void> {
    try {
      const response = await this.schemaService.getWorkflowCustomFields(workflowId);
      this.fieldsSignal.set(response.fields);
      const prevId = this.selectedPreviewFieldIdSignal();
      const ids = new Set(response.fields.map((f) => f.fieldId));
      if (prevId == null || !ids.has(prevId)) {
        this.selectedPreviewFieldIdSignal.set(null);
      }
    } catch {
      this.fieldsSignal.set([]);
      this.errorSignal.set(this.t('customFields.error.loadFailed'));
    } finally {
      this.loadingSignal.set(false);
    }
  }

  private validateFieldKeyUniqueness(field: CustomFieldViewModel, workflowId: string): boolean {
    const targetKey = this.toFieldKey(field.name);
    const hasConflict = this.fields().some((existing) => {
      if (field.fieldId && existing.fieldId === field.fieldId) {
        return false;
      }
      return this.toFieldKey(existing.name) === targetKey;
    });
    if (!hasConflict) return true;
    this.fieldMutationErrorSignal.set(this.t('customFields.error.duplicateFieldKey'));
    return false;
  }

  private toFieldKey(name: string): string {
    return name
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 64);
  }

  private needsOptions(type: CustomFieldType): boolean {
    return (type === 'SELECT' || type === 'ROW_COLOR');
  }
}
